import { isObject } from "../../../common/tools";
import { parseParameters } from "../../../exports/attribute-parser";
import type { Tracking } from "../../../ottoec/tracking-bct/nexus";
import {
  buildParameters,
  dataContainerParam,
  parserParametersOptions,
  type TrackingParametersMap,
} from "../parser/params.js";
import { lifecycleEvents, dispatchTrackingEvent } from "../event/event.js";
import { loggerScope } from "../parser/utils.js";
import { DATA_BASE_ATTR } from "../parser/elements.js";
import { PREFIX } from "../index.js";

const log = loggerScope.scope("listener");
const defaultMethod = "submitEvent" as keyof Tracking;

/**
 *
 *
 *
 *
 *
 */
export function getObjectFromProp<T extends object>(obj: object, prop: string): T {
  if (
    !isObject<Record<string, unknown>>(obj) ||
    !(prop in obj) ||
    !isObject<T>(obj[prop as never])
  ) {
    return {} as T;
  }

  return obj[prop] as T;
}

/**
 *
 *
 *
 *
 */
export function getParametersFromEvent(event: Event): TrackingParametersMap {
  const detail = getObjectFromProp(event, "detail");
  return getObjectFromProp(detail, PREFIX);
}

export function getOptionByPriority<T>(...args: unknown[]): T {
  return args.find((a) => a !== undefined) as never;
}

export type TrackingEventListenerContext = [element: HTMLElement, controller: AbortController];

/**
 *
 *
 *
 *
 */
export function trackingEventListener(
  this: TrackingEventListenerContext,
  trackedEvent: Event,
): void {
  const eventName = trackedEvent.type;
  const elementToTrack = (trackedEvent.currentTarget as HTMLElement) ?? this[0];
  log.info("Processing incomming tracking event", eventName, elementToTrack);

  /*                                                          */
  const {
    method: methodFromEvent,
    once: onceFromEvent,
    ...fromEvent
  } = getParametersFromEvent(trackedEvent);
  const {
    options = {},
    method,
    once,
    ...params
  } = parseParameters(DATA_BASE_ATTR, eventName, elementToTrack, parserParametersOptions, {
    extraParameters: fromEvent,
    expandUnnamedAs: dataContainerParam,
  });

  /*                                                */
  const trackingMethod = getOptionByPriority<keyof Tracking>(
    method,
    options.method,
    methodFromEvent,
    defaultMethod,
  );

  /*                                            */
  const trackOnce = getOptionByPriority<boolean>(once, options.once, onceFromEvent, false);

  /*                                                                    */
  const [parameters, parameterMap] = buildParameters(trackingMethod, params, options);

  log.debug("Trigger pre invocation lifecycle hook");
  if (
    !dispatchTrackingEvent(elementToTrack, lifecycleEvents[0], {
      event: trackedEvent,
      eventName,
      method: trackingMethod,
      parameters,
      parameterMap,
      abort: () => this[1].abort(eventName),
    } as never)
  ) {
    log.debug("Tracking invocation has been terminated by lifecycle hook", trackingMethod);
    return;
  }

  if (trackOnce) {
    log.debug("Tracking event is configured to be tracked once, abort", trackingMethod);
    this[1].abort(eventName);
  }

  log.info("Invoke tracking via", trackingMethod, parameters);
  /*                                             */
  otto.tracking[trackingMethod](...parameters);
}

export function initializeAbortSignal(
  elementToTrack: HTMLElement,
  listener: typeof trackingEventListener,
  signal: AbortSignal,
): void {
  /*                                                  */
  signal.addEventListener("abort", () => {
    const eventName = signal.reason as string;
    if (typeof eventName !== "string") {
      log.warn("Could not abort event listener, no event name provided");
      return;
    }

    /*                                                 */
    elementToTrack.removeEventListener(eventName, listener);
  });
}
